import { ApprovalConfigUI, TaskAction, TaskType } from '@/lib/sdk/chatbot/types';
import { GridRow } from './useTaskGridReducer';
import { readsRemovedTask } from './valueRef';

const COORD_REGEX = /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?(,\s*\d+(\.\d+)?)?$/;

// A task has an approver either through the flat fields (every workflow authored before
// levels existed, and single-level tasks that have never touched the levels editor) or
// through one of its levels (docs/maker-checker-design.md §7.2 — levels is authoritative
// once present, but nothing forces every level to be non-empty while it's still being
// authored, so this checks for at least one configured approver anywhere in the chain).
function hasAnyApprover(config?: ApprovalConfigUI): boolean {
  if (!config) return false;
  if (config.approvalContacts && config.approvalContacts.length > 0) return true;
  return (config.levels ?? []).some((level) => (level.approvalContacts ?? []).length > 0);
}

export function validateGridRows(
  rows: GridRow[]
): Map<string, Record<string, string>> {
  const errorMap = new Map<string, Record<string, string>>();

  for (const row of rows) {
    if (!row.name.trim() && !row.description.trim() && !row.data.trim()) continue;

    const errors: Record<string, string> = {};

    if (!row.name.trim()) {
      errors['name'] = 'Task name is required';
    } else if (row.name.trim().length > 24) {
      errors['name'] = 'Task name must be 24 characters or less';
    }

    if (row.description.length > 480) {
      errors['description'] = 'Description must be 480 characters or less';
    }

    if (!row.type) {
      errors['type'] = 'Task type is required';
    }

    const hasNearbyMasterDataSource =
      row.type === TaskType.LOCATION_CHECK &&
      row.dataSourceConfig?.source === 'master_data' &&
      !!row.dataSourceConfig?.dataset;

    if ((row.type === TaskType.LOCATION || row.type === TaskType.LOCATION_CHECK) && !hasNearbyMasterDataSource) {
      if (!row.data.trim()) {
        errors['data'] = 'Open Configure to set coordinates';
      } else if (!COORD_REGEX.test(row.data.trim())) {
        errors['data'] = 'Invalid coordinates — open Configure to fix';
      } else if (row.type === TaskType.LOCATION_CHECK) {
        const radius = Number(row.data.split(',')[2]?.trim() ?? 0);
        if (!(radius > 0)) {
          errors['data'] = 'Radius must be greater than 0 for Location Check — open Configure to fix';
        }
      }
    }

    if (row.type === TaskType.CHOICE && !row.data.trim()) {
      errors['data'] = 'Enter at least one choice option, one per line';
    }

    if (row.type === TaskType.DYNAMIC_CHOICE && !row.dataSourceConfig?.source) {
      errors['data'] = 'Open Configure to set a data source';
    }

    if (
      row.type === TaskType.COUNTER &&
      (!row.dataSourceConfig?.dataset || !row.dataSourceConfig?.lookupField)
    ) {
      errors['data'] = 'Open Configure to set a dataset and match field';
    }

    if (row.type === TaskType.TASKLIST && (!row.subtasks || row.subtasks.length === 0)) {
      errors['data'] = 'Open Configure to add subtasks';
    }

    if (row.actions?.includes(TaskAction.MAKERCHECKER) && !hasAnyApprover(row.approvalConfig)) {
      errors['data'] = 'Open Configure → add at least one approval contact for MakerChecker';
    }

    if (readsRemovedTask(row)) {
      errors['data'] = 'Open Configure → a value reads a task that was deleted or lost its name';
    }

    if (Object.keys(errors).length > 0) {
      errorMap.set(row._localId, errors);
    }
  }

  return errorMap;
}
